// phase-compiler-tab-pins (view presets) — the ONE canonical model for how the
// compiler workspace proportions its visible panes. Pins decide WHICH panes are
// visible (workspaceTabs.ts); this evaluator decides the PROPORTIONS, so the
// shell never forks the layout rule per surface.
//
// Three operator-chosen presets, persisted per submission alongside the pins:
//   • two-equal    — "Two equal panes (50/50)"
//   • thirds       — "Three equal panes (thirds)"
//   • half-quarters— "Half + quarters (50/25/25)"  (the default)
//
// The tree, when visible, is a resizable ratio column; the non-tree panes share
// the remaining width in a CSS grid whose column count + lead-pane span come
// from `resolveNonTreeGrid`. A lone non-tree pane always spans the whole non-tree
// area (the shell applies that) so "one visible pane → full width" and "two
// visible → 50/50" hold regardless of the grid's nominal column count.

export type WorkspaceViewPreset = 'two-equal' | 'thirds' | 'half-quarters';

export const WORKSPACE_VIEW_PRESETS: readonly WorkspaceViewPreset[] = [
  'two-equal',
  'thirds',
  'half-quarters',
];

/** The default preset: a half-width tree + quarter-width tiles beside it. */
export const DEFAULT_VIEW_PRESET: WorkspaceViewPreset = 'half-quarters';

/** The exact plain labels the View selector offers. */
export const VIEW_PRESET_LABELS: Readonly<Record<WorkspaceViewPreset, string>> = {
  'two-equal': 'Two equal panes (50/50)',
  thirds: 'Three equal panes (thirds)',
  'half-quarters': 'Half + quarters (50/25/25)',
};

export function isViewPreset(value: unknown): value is WorkspaceViewPreset {
  return typeof value === 'string' && WORKSPACE_VIEW_PRESETS.includes(value as WorkspaceViewPreset);
}

/** Validate a persisted value, falling back to the default for anything bad. */
export function parseViewPreset(value: unknown): WorkspaceViewPreset {
  return isViewPreset(value) ? value : DEFAULT_VIEW_PRESET;
}

/**
 * The tree column's fraction of the usable width for a preset, applied as the
 * reset ratio when the operator PICKS a preset. thirds gives the tree a third;
 * the other two give it half. The live ratio stays user-resizable afterwards.
 */
export function presetDefaultTreeRatio(preset: WorkspaceViewPreset): number {
  return preset === 'thirds' ? 1 / 3 : 0.5;
}

export interface LayoutInput {
  readonly preset: WorkspaceViewPreset;
  readonly treeVisible: boolean;
  /** How many NON-tree panes are currently visible (active ∪ pinned). */
  readonly nonTreeCount: number;
}

export interface NonTreeGrid {
  /** Number of equal (1fr) columns in the non-tree grid. */
  readonly columns: number;
  /** Grid-column span for the FIRST visible non-tree pane. */
  readonly leadSpan: number;
  /** Grid-column span for the remaining visible non-tree panes. */
  readonly otherSpan: number;
}

/**
 * Resolve the non-tree area's grid for the current preset, tree visibility and
 * visible-pane count. The shell still spans a LONE non-tree pane across the whole
 * area itself, so this only shapes the multi-pane cases.
 */
export function resolveNonTreeGrid(input: LayoutInput): NonTreeGrid {
  const { preset, treeVisible, nonTreeCount } = input;
  const uniform = (columns: number): NonTreeGrid => ({ columns, leadSpan: 1, otherSpan: 1 });

  if (nonTreeCount <= 1) {
    // A single (or no) non-tree pane fills the area; column count is immaterial.
    return uniform(1);
  }

  if (treeVisible) {
    // Beside the tree: two-equal stacks the right panes full width (one column);
    // thirds and half-quarters split the right area into two columns.
    return uniform(preset === 'two-equal' ? 1 : 2);
  }

  // No tree — the visible panes own the full width.
  if (nonTreeCount === 2) return uniform(2); // 50/50
  switch (preset) {
    case 'two-equal':
      return uniform(2); // two-column tiles, excess flows to further rows
    case 'thirds':
      return uniform(3);
    case 'half-quarters':
    default:
      // First pane half (span 2 of 4), the rest quarters (span 1) → 50/25/25.
      return { columns: 4, leadSpan: 2, otherSpan: 1 };
  }
}
